import { createContext, useContext, useEffect, useState, useMemo, useCallback } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DEFAULT_BRANDING = {
  primary: "#D65F79",
  primary_dark: "#B84A63",
  primary_soft: "#FDF2F5",
  accent: "#C49557",
  accent_soft: "#FBF5EC",
  surface: "#FDFBF7",
  surface_card: "#FFFFFF",
  surface_muted: "#F7F4EE",
  text_primary: "#1F1B16",
  text_muted: "#6B6258",
  border: "#EAE3D9",
  success: "#2E8F5A",
  success_soft: "#E8F5EE",
  warning: "#D49019",
  warning_soft: "#FBF1DC",
  danger: "#C8453F",
  danger_soft: "#FAE7E5",
  info: "#5174B5",
  info_soft: "#EAF0FB",
  logo_url: "",
  brand_name: "Celebra Mid",
  tagline: "Sistema operativo de eventos",
  font_display: "Cormorant Garamond",
  font_body: "Outfit",
  radius_card: 18,
  radius_button: 12,
};

const ThemeContext = createContext({
  branding: DEFAULT_BRANDING,
  setBranding: () => {},
  saveBranding: async () => {},
  loading: true,
});

export const useTheme = () => useContext(ThemeContext);

const applyCssVars = (b) => {
  const root = document.documentElement;
  const map = {
    "--brand-primary": b.primary,
    "--brand-primary-dark": b.primary_dark,
    "--brand-primary-soft": b.primary_soft,
    "--brand-accent": b.accent,
    "--brand-accent-soft": b.accent_soft,
    "--brand-surface": b.surface,
    "--brand-surface-card": b.surface_card,
    "--brand-surface-muted": b.surface_muted,
    "--brand-text": b.text_primary,
    "--brand-text-muted": b.text_muted,
    "--brand-border": b.border,
    "--brand-success": b.success,
    "--brand-success-soft": b.success_soft,
    "--brand-warning": b.warning,
    "--brand-warning-soft": b.warning_soft,
    "--brand-danger": b.danger,
    "--brand-danger-soft": b.danger_soft,
    "--brand-info": b.info,
    "--brand-info-soft": b.info_soft,
    "--brand-radius-card": `${b.radius_card}px`,
    "--brand-radius-button": `${b.radius_button}px`,
    "--brand-font-display": `'${b.font_display}', serif`,
    "--brand-font-body": `'${b.font_body}', sans-serif`,
  };
  Object.entries(map).forEach(([k, v]) => v && root.style.setProperty(k, v));
};

export const ThemeProvider = ({ children, user, publicUsuarioId = null }) => {
  const [branding, setBrandingState] = useState(DEFAULT_BRANDING);
  const [loading, setLoading] = useState(true);

  const loadBranding = useCallback(async () => {
    try {
      let resp;
      if (publicUsuarioId) {
        resp = await axios.get(`${API}/branding/publico/${publicUsuarioId}`);
      } else if (user) {
        resp = await axios.get(`${API}/branding`);
      } else {
        setBrandingState(DEFAULT_BRANDING);
        applyCssVars(DEFAULT_BRANDING);
        setLoading(false);
        return;
      }
      const b = { ...DEFAULT_BRANDING, ...resp.data };
      setBrandingState(b);
      applyCssVars(b);
    } catch {
      setBrandingState(DEFAULT_BRANDING);
      applyCssVars(DEFAULT_BRANDING);
    } finally {
      setLoading(false);
    }
  }, [user, publicUsuarioId]);

  useEffect(() => { loadBranding(); }, [loadBranding]);

  const setBranding = useCallback((b) => {
    const merged = { ...branding, ...b };
    setBrandingState(merged);
    applyCssVars(merged);
  }, [branding]);

  const saveBranding = useCallback(async (b) => {
    const merged = { ...branding, ...b };
    const { data } = await axios.put(`${API}/branding`, merged);
    const final = { ...DEFAULT_BRANDING, ...(data.branding || merged) };
    setBrandingState(final);
    applyCssVars(final);
    return final;
  }, [branding]);

  const value = useMemo(() => ({ branding, setBranding, saveBranding, loading }), [branding, setBranding, saveBranding, loading]);

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
};

export default ThemeContext;
